import React, { useEffect, useState } from "react";
import { CheckCircleIcon, SparklesIcon, ArrowRightIcon } from "lucide-react";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { cn } from "@/lib/utils";

interface CheckoutSuccessPageProps {
  /**
   * Cohort name
   */
  cohortName?: string;

  /**
   * Dashboard link (from component prop)
   */
  dashboardHref?: string;

  /**
   * Dashboard URL (from ERB)
   */
  dashboardUrl?: string;

  /**
   * Start date (from ERB)
   */
  startDate?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

// Confetti particle component
function ConfettiParticle({ delay }: { delay: number }) {
  const colors = [
    "bg-primary",
    "bg-orange-400",
    "bg-green-500",
    "bg-yellow-500",
    "bg-pink-500",
    "bg-purple-500",
  ];

  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  const randomX = Math.random() * 100;
  const randomRotation = Math.random() * 360;
  const randomDuration = 2 + Math.random() * 2;

  return (
    <div
      className={cn("absolute w-2 h-2 rounded-full opacity-0", randomColor)}
      style={{
        left: `${randomX}%`,
        top: "-10px",
        animation: `confetti-fall ${randomDuration}s ease-in ${delay}s forwards`,
        transform: `rotate(${randomRotation}deg)`,
      }}
    />
  );
}

export function CheckoutSuccessPage({
  cohortName = "Customer Discovery Fundamentals",
  dashboardHref = "/dashboard",
  dashboardUrl,
  startDate,
  className = "",
}: CheckoutSuccessPageProps) {
  // Prefer ERB-provided dashboardUrl over prop default
  const resolvedDashboardHref = dashboardUrl || dashboardHref;
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Hide confetti after animation completes
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={cn(
        "min-h-screen bg-background relative overflow-hidden",
        className
      )}
    >
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {Array.from({ length: 50 }).map((_, i) => (
            <ConfettiParticle key={i} delay={i * 0.05} />
          ))}
        </div>
      )}

      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-bold text-primary-foreground">
                  BL
                </span>
              </div>
              <span className="font-semibold text-foreground">BitLearn</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-3xl mx-auto px-4 py-16">
        <div className="text-center space-y-8">
          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Animated rings */}
              <div className="absolute inset-0 animate-ping">
                <div className="h-24 w-24 rounded-full bg-green-500/20" />
              </div>
              <div className="absolute inset-0 animate-pulse">
                <div className="h-24 w-24 rounded-full bg-green-500/10" />
              </div>
              {/* Icon */}
              <div className="relative h-24 w-24 rounded-full bg-green-500 flex items-center justify-center">
                <CheckCircleIcon className="h-12 w-12 text-white" />
              </div>
            </div>
          </div>

          {/* Welcome Message */}
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2">
              <SparklesIcon className="h-6 w-6 text-primary" />
              <h1 className="text-4xl font-bold text-foreground">
                Welcome to {cohortName}!
              </h1>
              <SparklesIcon className="h-6 w-6 text-primary" />
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Your enrollment is confirmed! Get ready to start your learning
              journey.
            </p>
          </div>

          {/* Success Details */}
          <div className="bg-card border border-border rounded-lg p-8 space-y-6 max-w-xl mx-auto">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-foreground">
                    Payment Successful
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Your payment has been processed successfully
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-foreground">
                    Confirmation Email Sent
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Check your inbox for enrollment details and next steps
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-foreground">Instant Access</p>
                  <p className="text-sm text-muted-foreground">
                    All course materials are now available in your dashboard
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <a href={resolvedDashboardHref}>
              <AcademicianStartupButton
                variant="primary"
                size="lg"
                rightIcon={<ArrowRightIcon className="h-4 w-4" />}
              >
                Go to Dashboard
              </AcademicianStartupButton>
            </a>
            <a href="/cohorts">
              <AcademicianStartupButton variant="secondary" size="lg">
                Browse More Cohorts
              </AcademicianStartupButton>
            </a>
          </div>

          {/* Additional Info */}
          <div className="pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Need help? Contact us at{" "}
              <a
                href="mailto:support@academicianstartup.com"
                className="text-primary hover:underline"
              >
                support@academicianstartup.com
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Confetti Animation Styles */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
